import React, { useState, useEffect } from 'react';
import {
  ArrowDownTrayIcon,
  CloudArrowDownIcon,
  FolderIcon,
  CloudIcon,
  BuildingOffice2Icon,
  CircleStackIcon,
  DocumentArrowDownIcon,
  InformationCircleIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import ConfirmationModal from '../components/ConfirmationModal';
import DownloadProgressModal from '../components/DownloadProgressModal';
import DatabaseBackup from '../components/DatabaseBackup';
import CategoryManagementModal from '../components/modals/CategoryManagementModal';
import { useDocumentsDownloadProgress, useDogsDownloadProgress } from '../hooks/useDownloadProgress';
import apiClient from '../utils/apiClient';

interface ConfirmModalState {
  visible: boolean;
  type: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant: 'single' | 'two';
  onClose?: () => void;
  buttons?: {
    cancel: { text: string; onPress: () => void };
    confirm: { text: string; onPress: () => void };
  };
  button?: { text: string; onPress: () => void };
}

const AdminFinanceExport: React.FC = () => {
  const documentsDownload = useDocumentsDownloadProgress();
  const dogsDownload = useDogsDownloadProgress();
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    visible: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });

  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedExportYear, setSelectedExportYear] = useState<number>(new Date().getFullYear());
  const [includeDocuments, setIncludeDocuments] = useState<boolean>(true);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [finanzamtExportLoading, setFinanzamtExportLoading] = useState<boolean>(false);

  const [selectedChapters, setSelectedChapters] = useState({
    mitgliederliste: true,
    hundeuebersicht: true,
    finanzuebersicht: true,
    finanzamt: true,
    dokumente: true
  });

  const [selectedFinanzamtChapters, setSelectedFinanzamtChapters] = useState({
    einnahmenAusgaben: true,
    vermoegensaufstellung: true,
    taetigkeitsbericht: true,
    veranstaltungsstatistik: true,
    ruecklagennachweis: true
  });
  const [showFinanzamtHelp, setShowFinanzamtHelp] = useState(false);

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  const fetchAvailableYears = async () => {
    try {
      const response = await apiClient.get('/api/backup/yearly-report/years');
      if (response.data.success && response.data.data) {
        setAvailableYears(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedExportYear(response.data.data[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching available years:', err);
      const currentYear = new Date().getFullYear();
      setAvailableYears([currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4]);
    }
  };

  const handleDownloadAll = () => documentsDownload.startDownload();
  const handleDownloadAllMembersDogs = () => dogsDownload.startDownload();

  const handleYearlyReportExport = async () => {
    setExportLoading(true);
    try {
      const chaptersParam = encodeURIComponent(JSON.stringify(selectedChapters));
      const response = await apiClient.get(
        `/api/backup/yearly-report?year=${selectedExportYear}&includeDocuments=${includeDocuments}&chapters=${chaptersParam}`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Jahresordner_${selectedExportYear}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setConfirmModal({
        visible: true, type: 'success', title: 'Export erfolgreich',
        message: `Der Jahresordner für ${selectedExportYear} wurde erfolgreich erstellt und heruntergeladen.`,
        variant: 'single', button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } catch (err: any) {
      setConfirmModal({
        visible: true, type: 'error', title: 'Export fehlgeschlagen',
        message: err.response?.data?.message || 'Fehler beim Erstellen des Jahresordners.',
        variant: 'single', button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleFinanzamtExport = async () => {
    setFinanzamtExportLoading(true);
    try {
      const chaptersParam = encodeURIComponent(JSON.stringify(selectedFinanzamtChapters));
      const response = await apiClient.get(
        `/api/backup/finanzamt-report?year=${selectedExportYear}&chapters=${chaptersParam}`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Finanzamt_Unterlagen_${selectedExportYear}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setConfirmModal({
        visible: true, type: 'success', title: 'Export erfolgreich',
        message: `Die Finanzamt-Unterlagen für ${selectedExportYear} wurden erfolgreich erstellt.`,
        variant: 'single', button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } catch (err: any) {
      setConfirmModal({
        visible: true, type: 'error', title: 'Export fehlgeschlagen',
        message: err.response?.data?.message || 'Fehler beim Erstellen der Finanzamt-Unterlagen.',
        variant: 'single', button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } finally {
      setFinanzamtExportLoading(false);
    }
  };

  const cardClass = 'bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700';
  const subTileClass = 'bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700';
  const inputClass = 'bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700';
  const primaryBtn = 'bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _secondaryBtn = 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-white/10 font-semibold rounded-xl transition';

  return (
    <div className="min-h-screen font-sans text-gray-900 dark:text-slate-200" style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}>
      <div className="max-w-7xl mx-auto py-2 sm:py-4">
        {/* Hero */}
        <section className="pt-2 pb-6 px-4 sm:px-6">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600/80 dark:text-amber-400/80 font-semibold mb-3">
              ADMIN · Backup & Export
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.04em' }}>
              Backup-<br /><span className="text-amber-600 dark:text-amber-400">Tresor.</span>
            </h1>
            <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
              Komplettsicherung von Datenbank, Dokumenten und Hunde-Unterlagen — plus
              <span className="font-bold text-amber-700 dark:text-amber-300"> Jahresordner</span> und <span className="font-bold text-emerald-700 dark:text-emerald-300">Finanzamt-Unterlagen</span> als PDF auf Knopfdruck.
            </p>
          </div>
        </section>

        <div className="px-4 sm:px-6 space-y-6">
          {/* Backup-Quellen */}
          <section className={`rounded-3xl p-6 ${cardClass}`}>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2.5 mb-4">
              <span className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                <CloudIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </span>
              Backup-Quellen
            </h2>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-6">Erstellen Sie Backups Ihrer Dokumente.</p>

            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <CloudArrowDownIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                Datensicherung
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
                Erstellen Sie ein Backup aller Dokumente oder der Hunde-Dokumente aller Mitglieder.
                Die Dateien werden von Amazon S3 heruntergeladen, bei Bedarf entschlüsselt und unverschlüsselt auf Ihrem Gerät gespeichert.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <button
                  onClick={handleDownloadAll}
                  disabled={documentsDownload.progress.active || dogsDownload.progress.active}
                  className={`${primaryBtn} px-4 py-2.5 w-full sm:w-auto flex items-center justify-center gap-2`}
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span className="text-sm">Alle Dokumente sichern</span>
                </button>
                <button
                  onClick={handleDownloadAllMembersDogs}
                  disabled={documentsDownload.progress.active || dogsDownload.progress.active}
                  className={`${primaryBtn} px-4 py-2.5 w-full sm:w-auto flex items-center justify-center gap-2`}
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span className="text-sm">Alle Hunde-Dokumente sichern</span>
                </button>
              </div>

              <div className="pt-6 border-t border-gray-200 dark:border-slate-800">
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <CircleStackIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  Datenbank-Backup
                </h3>
                <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
                  Erstellen Sie ein vollständiges Backup der Datenbank im JSON-Format oder importieren Sie ein vorhandenes Backup.
                </p>
                <DatabaseBackup />
              </div>
            </div>
          </section>

          {/* Finanzkategorien */}
          <section className={`rounded-3xl p-6 ${cardClass}`}>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2.5 mb-4">
              <span className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                <TagIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </span>
              Finanzkategorien
            </h2>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-6">
              Verwalten Sie die Kategorien für Buchungen und ordnen Sie ihnen steuerliche Sphären zu
              (ideell, Vermögen, Zweckbetrieb, wirtschaftlich) für die Steuererklärung.
            </p>
            <button
              onClick={() => setShowCategoryModal(true)}
              className={`${primaryBtn} px-4 py-2.5 w-full sm:w-auto flex items-center justify-center gap-2`}
            >
              <TagIcon className="h-5 w-5" />
              Kategorien verwalten
            </button>
          </section>

          {/* Jahresordner Export */}
          <section className={`rounded-3xl p-6 ${cardClass}`}>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2.5 mb-4">
              <span className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                <DocumentArrowDownIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </span>
              Jahresordner Export
            </h2>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-6">
              Erstellen Sie einen kompletten Jahresordner als PDF mit Deckblatt, Inhaltsverzeichnis,
              Mitgliederliste, Hundeübersicht, Kontobuchungen und allen Dokumenten des Jahres.
            </p>

            {/* Jahr Auswahl */}
            <div className={`rounded-2xl p-4 mb-6 ${subTileClass}`}>
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">
                Jahr auswählen
              </label>
              <select
                value={selectedExportYear}
                onChange={(e) => setSelectedExportYear(Number(e.target.value))}
                className={`w-full sm:w-48 px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none ${inputClass}`}
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Linke Spalte */}
              <div className={`rounded-2xl p-5 flex flex-col ${subTileClass}`}>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FolderIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  Kompletter Jahresordner
                </h3>
                <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">Wählen Sie die Kapitel, die enthalten sein sollen:</p>

                <div className="space-y-2 mb-5">
                  {[
                    { key: 'mitgliederliste', label: 'Mitgliederliste' },
                    { key: 'hundeuebersicht', label: 'Hundeübersicht' },
                    { key: 'finanzuebersicht', label: 'Finanzübersicht (Kategorien + Kontobuchungen)' },
                    { key: 'finanzamt', label: 'Unterlagen für das Finanzamt' },
                    { key: 'dokumente', label: 'Dokumente anhängen (PDFs aus AWS S3)' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-white/5 p-2 rounded-lg transition-colors">
                      <input
                        type="checkbox"
                        checked={(selectedChapters as any)[key]}
                        onChange={(e) => {
                          setSelectedChapters(prev => ({ ...prev, [key]: e.target.checked }));
                          if (key === 'dokumente') setIncludeDocuments(e.target.checked);
                        }}
                        className="w-5 h-5 accent-amber-500 rounded"
                      />
                      <span className="text-sm text-gray-800 dark:text-slate-200">{label}</span>
                    </label>
                  ))}
                </div>

                <button
                  onClick={handleYearlyReportExport}
                  disabled={exportLoading || !Object.values(selectedChapters).some(v => v)}
                  className={`${primaryBtn} mt-auto w-full px-6 py-3 flex items-center justify-center gap-2`}
                >
                  {exportLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Wird erstellt...
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="h-5 w-5" />
                      Jahresordner {selectedExportYear} exportieren
                    </>
                  )}
                </button>
              </div>

              {/* Rechte Spalte: Finanzamt */}
              <div className="rounded-2xl p-5 flex flex-col" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <BuildingOffice2Icon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    Finanzamt-Unterlagen
                  </h3>
                  <button
                    onClick={() => setShowFinanzamtHelp(!showFinanzamtHelp)}
                    className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 transition-colors"
                  >
                    <InformationCircleIcon className="h-4 w-4" />
                    {showFinanzamtHelp ? 'Hilfe ausblenden' : 'Was wird benötigt?'}
                  </button>
                </div>

                {showFinanzamtHelp && (
                  <div className="rounded-xl p-4 mb-4 bg-white/70 dark:bg-slate-950/50 border border-amber-500/30">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-sm">Überprüfung der Geschäftsführung (§ 63 AO)</h4>
                    <p className="text-xs text-gray-600 dark:text-slate-400 mb-3">
                      Das Finanzamt für Körperschaften prüft regelmäßig die Gemeinnützigkeit. Folgende Unterlagen werden typischerweise angefordert:
                    </p>
                    <ul className="text-xs text-gray-600 dark:text-slate-400 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-700 dark:text-amber-300 font-black">1.</span>
                        <span><strong className="text-gray-800 dark:text-slate-200">Einnahmen-Ausgaben-Rechnung</strong> – getrennt nach den 4 Sphären: Ideeller Bereich, Vermögensverwaltung, Zweckbetrieb, wirtschaftlicher Geschäftsbetrieb</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-700 dark:text-amber-300 font-black">2.</span>
                        <span><strong className="text-gray-800 dark:text-slate-200">Tätigkeitsbericht</strong> – Protokolle der Mitgliederversammlungen, Veranstaltungsprogramme</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-700 dark:text-amber-300 font-black">3.</span>
                        <span><strong className="text-gray-800 dark:text-slate-200">Vermögensaufstellung</strong> – Vermögen zum 31.12. (Bankguthaben, Inventar, Forderungen)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-700 dark:text-amber-300 font-black">4.</span>
                        <span><strong className="text-gray-800 dark:text-slate-200">Rücklagennachweis</strong> – Bildung und Entwicklung der Rücklagen (§ 62 AO)</span>
                      </li>
                    </ul>
                  </div>
                )}

                <p className="text-sm text-gray-700 dark:text-slate-300 mb-4">Unterlagen für die Überprüfung der Gemeinnützigkeit:</p>

                <div className="space-y-2 mb-5">
                  {[
                    { key: 'einnahmenAusgaben', label: 'Einnahmen-Ausgaben-Rechnung', sub: 'Getrennt nach 4 Sphären (ideell, Vermögen, Zweckbetrieb, wirtschaftlich)' },
                    { key: 'vermoegensaufstellung', label: 'Vermögensaufstellung zum 31.12.', sub: 'Bankguthaben, Inventar, Forderungen, Verbindlichkeiten' },
                    { key: 'taetigkeitsbericht', label: 'Tätigkeitsbericht', sub: 'Protokolle, Veranstaltungen, Aktivitäten im Prüfungszeitraum' },
                    { key: 'veranstaltungsstatistik', label: 'Veranstaltungsstatistik', sub: 'Übersicht aller Veranstaltungen mit Teilnehmerzahlen' },
                    { key: 'ruecklagennachweis', label: 'Rücklagennachweis', sub: 'Bildung und Entwicklung der Rücklagen nach § 62 AO' },
                  ].map(({ key, label, sub }) => (
                    <label key={key} className="flex items-start gap-3 cursor-pointer hover:bg-amber-500/10 p-2 rounded-lg transition-colors">
                      <input
                        type="checkbox"
                        checked={(selectedFinanzamtChapters as any)[key]}
                        onChange={(e) => setSelectedFinanzamtChapters(prev => ({ ...prev, [key]: e.target.checked }))}
                        className="w-5 h-5 mt-0.5 accent-amber-500 rounded"
                      />
                      <div>
                        <span className="text-sm text-gray-900 dark:text-slate-100 font-bold">{label}</span>
                        <p className="text-xs text-gray-600 dark:text-slate-400">{sub}</p>
                      </div>
                    </label>
                  ))}
                </div>

                <button
                  onClick={handleFinanzamtExport}
                  disabled={finanzamtExportLoading || !Object.values(selectedFinanzamtChapters).some(v => v)}
                  className={`${primaryBtn} mt-auto w-full px-6 py-3 flex items-center justify-center gap-2`}
                >
                  {finanzamtExportLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Wird erstellt...
                    </>
                  ) : (
                    <>
                      <BuildingOffice2Icon className="h-5 w-5" />
                      Finanzamt-Unterlagen {selectedExportYear}
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Documents Download Progress */}
      <DownloadProgressModal
        visible={documentsDownload.progress.active || (!!documentsDownload.progress.error && !documentsDownload.progress.active)}
        progress={documentsDownload.progress}
        onCancel={documentsDownload.cancelDownload}
        onClose={() => {
          const wasSuccessful = !documentsDownload.progress.active && !documentsDownload.progress.error;
          documentsDownload.resetProgress();
          if (wasSuccessful) {
            setConfirmModal({
              visible: true, type: 'success', title: 'Erfolgreich',
              message: 'Alle Dokumente wurden heruntergeladen!', variant: 'single',
              button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
            });
          }
        }}
      />

      {/* Dogs Download Progress */}
      <DownloadProgressModal
        visible={dogsDownload.progress.active || (!!dogsDownload.progress.error && !dogsDownload.progress.active)}
        progress={dogsDownload.progress}
        onCancel={dogsDownload.cancelDownload}
        onClose={() => {
          const wasSuccessful = !dogsDownload.progress.active && !dogsDownload.progress.error;
          dogsDownload.resetProgress();
          if (wasSuccessful) {
            setConfirmModal({
              visible: true, type: 'success', title: 'Erfolgreich',
              message: 'Alle Hunde-Dokumente wurden heruntergeladen!', variant: 'single',
              button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
            });
          }
        }}
      />

      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={confirmModal.onClose}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        buttons={confirmModal.buttons}
        button={confirmModal.button}
      />

      <CategoryManagementModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
      />
    </div>
  );
};

export default AdminFinanceExport;
